// src/pages/UsersAccessStaticPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Switch,
  IconButton,
  useMediaQuery,
  useTheme,
  InputLabel,
} from "@mui/material";
import { Add, Edit, Delete, Link as LinkIcon } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const API = process.env.REACT_APP_API_URL + "/access";
const roles = ["admin", "agent", "client"];

const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f9f9f9", paper: "#FFFFFF" },
    primary: { main: "#C4B08C" },
    error: { main: "#d32f2f" },
    text: { primary: "#111", secondary: "#555" },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  titre: string;
  actif: boolean;
  verified: boolean;
}

const UsersAccessStaticPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  /* modals */
  const [modal, setModal] = useState<"add" | "edit" | "delete" | null>(null);
  const [form, setForm] = useState<any>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({
    open: false,
    msg: "",
    severity: "success",
  });

  const [linkSnack, setLinkSnack] = useState(false);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  /* ---------- API ---------- */
  const fetchUsers = async () => {
    try {
      const res = await fetch(API);
      const data = await res.json();
      console.log("API Response:", data); // Debug log
      // Ensure we always set an array
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (data && Array.isArray(data.users)) {
        setUsers(data.users);
      } else if (data && Array.isArray(data.data)) {
        setUsers(data.data);
      } else {
        console.error("Unexpected API response format:", data);
        setUsers([]);
        setSnack({
          open: true,
          msg: "Unexpected data format from server",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setUsers([]); // Set empty array on error
      setSnack({ open: true, msg: "Failed to load users", severity: "error" });
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  /* ---------- CRUD ---------- */
  const openModal = (type: "add" | "edit" | "delete", u?: User) => {
    if (type === "delete" && u) {
      setDeleteId(u.id); // store id only
      setModal("delete");
      return;
    }
    setModal(type);
    setForm(
      type === "delete"
        ? {}
        : u
          ? { ...u }
          : {
              firstname: "",
              lastname: "",
              email: "",
              phone: "",
              titre: "agent",
              actif: true,
              verified: false,
            },
    );
  };
  const closeModal = () => {
    setModal(null);
    setDeleteId(null);
  };

  const handleSave = async () => {
    const url = form.id ? `${API}/${form.id}` : API;
    const method = form.id ? "PUT" : "POST";
    try {
      await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSnack({
        open: true,
        msg: form.id ? "User updated" : "User added",
        severity: "success",
      });
      fetchUsers();
      closeModal();
    } catch {
      setSnack({ open: true, msg: "Save failed", severity: "error" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API}/${deleteId}`, { method: "DELETE" });
      setSnack({ open: true, msg: "User deleted", severity: "success" });
      fetchUsers();
    } catch {
      setSnack({ open: true, msg: "Delete failed", severity: "error" });
    }
    closeModal();
  };

  const patchToggle = (
    id: string,
    field: "actif" | "verified" | "titre",
    value: any,
  ) => {
    const url =
      field === "titre" ? `${API}/${id}/title` : `${API}/${id}/verify`;

    fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    })
      .then(fetchUsers)
      .catch(() =>
        setSnack({ open: true, msg: "Toggle failed", severity: "error" }),
      );
  };
  const copyRegisterLink = (u: User) => {
    const url = `${process.env.REACT_APP_API_URL}/register?email=${encodeURIComponent(
      u.email,
    )}&firstname=${encodeURIComponent(
      u.firstname,
    )}&lastname=${encodeURIComponent(u.lastname)}&phone=${encodeURIComponent(
      u.phone || "",
    )}&id=${encodeURIComponent(u.id || "")}`;
    navigator.clipboard.writeText(url);
    setLinkSnack(true);
  };

  /* ---------- Filtering ---------- */
  const filtered = users.filter(
    (u) =>
      (roleFilter === "all" || u.titre === roleFilter) &&
      (u.firstname.toLowerCase().includes(search.toLowerCase()) ||
        u.lastname.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
          👤 Users Management
        </Typography>

        {/* Search + role chips + Add button */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          mb={3}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, borderRadius: 2 }}
          />
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label="All"
              color={roleFilter === "all" ? "primary" : "default"}
              onClick={() => setRoleFilter("all")}
            />
            {roles.map((r) => (
              <Chip
                key={r}
                label={r}
                color={roleFilter === r ? "primary" : "default"}
                onClick={() => setRoleFilter(r)}
              />
            ))}
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => openModal("add")}
          >
            Add User
          </Button>
        </Box>

        {/* Responsive table / cards */}
        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {filtered.map((u) => (
              <Card key={u.id} sx={{ boxShadow: 3, borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6">{`${u.firstname} ${u.lastname}`}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {u.email}
                  </Typography>
                  <Typography variant="body2">{u.phone || "—"}</Typography>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                  >
                    <Chip label={u.titre} size="small" />
                    <Box display="flex" gap={1} alignItems="center">
                      <Switch
                        checked={u.actif}
                        onChange={() => patchToggle(u.id, "actif", !u.actif)}
                        color="primary"
                      />
                      <Switch
                        checked={u.verified}
                        onChange={() =>
                          patchToggle(u.id, "verified", !u.verified)
                        }
                        color="success"
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={1} mt={2}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => openModal("edit", u)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => openModal("delete", u)}
                    >
                      Delete
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => copyRegisterLink(u)}
                    >
                      Link
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 4, boxShadow: 3 }}
          >
            <Table>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  {[
                    "First Name",
                    "Last Name",
                    "Email",
                    "Phone",
                    "Role",
                    "Verified",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.firstname}</TableCell>
                    <TableCell>{u.lastname}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.phone || "—"}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={u.titre}
                        onChange={(e) =>
                          patchToggle(u.id, "titre", e.target.value)
                        }
                      >
                        {roles.map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={u.verified}
                        onChange={() =>
                          patchToggle(u.id, "verified", !u.verified)
                        }
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => openModal("edit", u)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => openModal("delete", u)}
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => copyRegisterLink(u)}
                      >
                        <Add />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog
          open={modal === "delete"}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              closeModal();
            }
          }}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this user?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add / Edit Modal */}
        <Dialog
          open={modal === "add" || modal === "edit"}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              closeModal();
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {modal === "add" ? "Add User" : "Edit User"}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="First Name"
              fullWidth
              value={form.firstname || ""}
              onChange={(e) => setForm({ ...form, firstname: e.target.value })}
              sx={{ mb: 2, mt: 2 }}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={form.lastname || ""}
              onChange={(e) => setForm({ ...form, lastname: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              fullWidth
              value={form.email || ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Phone"
              fullWidth
              value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.titre || "agent"}
                onChange={(e) => setForm({ ...form, titre: e.target.value })}
              >
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" gap={2} alignItems="center">
              <Typography>Active</Typography>
              <Switch
                checked={form.actif ?? true}
                onChange={() => setForm({ ...form, actif: !form.actif })}
                color="primary"
              />
              <Typography>Verified</Typography>
              <Switch
                checked={form.verified ?? false}
                onChange={() => setForm({ ...form, verified: !form.verified })}
                color="success"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#C4B08C", color: "#000" }}
              onClick={handleSave}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          <Alert severity={snack.severity}>{snack.msg}</Alert>
        </Snackbar>
        <Snackbar
          open={linkSnack}
          autoHideDuration={2000}
          onClose={() => setLinkSnack(false)}
          message="Registration link copied!"
        />
      </Container>
    </ThemeProvider>
  );
};

export default UsersAccessStaticPage;
